import { Link } from "@inertiajs/react"
import { ScrollReveal } from "@/components/ui/scroll-reveal"

export type RelatedLink = {
  href: string
  title: string
  description: string
}

type Tone = "cream" | "white"

interface RelatedPagesProps {
  links: RelatedLink[]
  heading?: string
  eyebrow?: string
  tone?: Tone
}

export function RelatedPages({
  links,
  heading = "Keep reading",
  eyebrow = "Related pages",
  tone = "cream",
}: RelatedPagesProps) {
  if (links.length === 0) return null

  const sectionBg = tone === "cream" ? "bg-cream" : "bg-white"

  return (
    <section className={`border-t border-stone ${sectionBg} px-6 py-20`}>
      <div className="mx-auto max-w-5xl">
        <ScrollReveal>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            {eyebrow}
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold text-charcoal md:text-3xl">
            {heading}
            <span className="text-red">.</span>
          </h2>
        </ScrollReveal>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((link) => (
            <ScrollReveal key={link.href}>
              <Link
                href={link.href}
                className="group flex h-full flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {link.title}
                </p>
                <p className="mt-1 flex-1 text-sm leading-relaxed text-slate">
                  {link.description}
                </p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read more
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  )
}

export default RelatedPages
